import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Button, Checkbox, Dialog, useShowSnackbar } from "@octopusdeploy/design-system-components";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import { borderWidth, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { useAggregateAPIOperationStatus, useMutation, useOctopusClient } from "@octopusdeploy/octopus-react-client";
import type { ProjectTemplateProcessResource, ModifyProjectTemplateProcessCommand, GitRefResource, ProjectTemplateParameterSetResource, VariableSetResource, ProjectTemplateVariableSetResource, VariableResource, ActionTemplateResource, ModifyProjectTemplateParameterSetCommand, } from "@octopusdeploy/octopus-server-client";
import { Repository, mapFromEnvironmentResourceToEnvironmentV2Resource, Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { semanticConventions } from "@octopusdeploy/telemetry";
import { noOp } from "@octopusdeploy/utilities";
import { isEqual, keyBy } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { applyCommonLogicToProducerProcessResource } from "~/areas/platformHub/PlatformHubProducerProcessHelpers";
import { ProjectTemplateCommitDialog } from "~/areas/platformHub/projectTemplates/ProjectTemplateCommitDialog";
import ProjectTemplatePublishButton from "~/areas/platformHub/projectTemplates/ProjectTemplatePublishButton";
import { projectTemplateStatusActions, type ProjectTemplateStatus } from "~/areas/platformHub/projectTemplates/ProjectTemplateStatus";
import { useProjectTemplateStatusContext } from "~/areas/platformHub/projectTemplates/ProjectTemplateStatusContext";
import { ProjectTemplateWizardDialog, TemplateWizardSteps } from "~/areas/platformHub/projectTemplates/ProjectTemplateWizardDialog";
import useProjectTemplateDeploymentProcessState from "~/areas/platformHub/projectTemplates/useProjectTemplateDeploymentProcessState";
import { useProjectTemplateWizard, useIsProjectTemplateShared } from "~/areas/platformHub/projectTemplates/useProjectTemplateWizard";
import { useSaveProjectTemplate } from "~/areas/platformHub/projectTemplates/useSaveProjectTemplate";
import { useStopShareProjectTemplate } from "~/areas/platformHub/projectTemplates/useStopShareProjectTemplate";
import type { AddStepTemplateResult } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { ProcessController } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorActions, useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { createDefaultFilter, ProcessQueryStringController, useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessSearchFilterContext } from "~/areas/projects/components/Process/Contexts/ProcessSearchFilter/ProcessSearchFilterContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { ProcessListAdvancedFilters } from "~/areas/projects/components/Process/CustomPaperLayouts/ProcessContextPaperLayout";
import ProcessList from "~/areas/projects/components/Process/ProcessList";
import ProcessStepsLayout, { ProcessPageIntent, intentFromFilter } from "~/areas/projects/components/Process/ProcessStepsLayout";
import { emptyVariableSetResource, type ProcessStepsLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import type { ProducerProjectTemplateContextProps } from "~/areas/projects/components/Process/ProjectTemplates/ProducerProjectTemplateContext";
import { buildProducerProjectTemplateContextProps, ProducerProjectTemplateContext } from "~/areas/projects/components/Process/ProjectTemplates/ProducerProjectTemplateContext";
import { useProducerTemplateProcessContext } from "~/areas/projects/components/Process/ProjectTemplates/useProducerTemplateProcessContext";
import type { ProcessPageSupportedActions } from "~/areas/projects/components/Process/types";
import { ContextAddStepButton } from "~/areas/projects/components/Steps/index";
import { PersistenceSettingsContextProvider } from "~/areas/projects/context/PersistenceSettingsContext";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import * as tagSets from "~/components/tagSets";
import type { ProjectTemplateContextProps } from "~/routing/pageRegistrations/ProjectTemplatePageRegistration";
import type { GlobalState } from "~/store/reducers";
import type { ImportStepTemplateResult } from "../StepTemplateImportedCallout";
import { convertActionTemplateParametersToTemplateParameters, StepTemplateImportedCallout } from "../StepTemplateImportedCallout";
import { StopShareTemplateDialog } from "../StopShareTemplateDialog";
import createProjectTemplateOverflowMenuItems, { PlatformHubTemplateType } from "../platformHubHelpers";
import { GetProjectTemplateInitialSuccessCallout } from "./ProjectTemplateLayout";
export interface ProjectTemplateDeploymentProcessQueryParams {
    actionId?: string;
    parentStepId?: string;
    actionType?: string;
    new?: boolean;
    stepTemplates?: boolean;
    childStepTemplates?: boolean;
    templateId?: string;
    templateSpaceId?: string;
    isCommunityTemplate?: boolean;
    actionCategory?: string;
    newlyCreatedProject?: boolean;
    configureProject?: boolean;
    loadSampleProcess?: string;
}
interface ProjectTemplateDeploymentProcessPageProps {
    loaderData: LoaderData;
    gitRef: string;
    queryParams: ProjectTemplateDeploymentProcessQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectTemplateDeploymentProcessQueryParams>;
    projectTemplateContext: ProjectTemplateContextProps;
}
type LoaderData = {
    deploymentProcess: ProjectTemplateProcessResource;
    parameters: ProjectTemplateParameterSetResource;
    variableSet: VariableSetResource;
};
export async function projectTemplateDeploymentProcessPageLoader(repository: Repository, gitRef: string, slug: string): Promise<LoaderData> {
    const [deploymentProcess, parameters, variableSet] = await Promise.all([
        repository.ProjectTemplates.getDeploymentProcess(slug, gitRef),
        repository.ProjectTemplates.tryGetParameterSet(slug, gitRef),
        await getProjectTemplateVariableSet(repository, slug, gitRef),
    ]);
    return {
        deploymentProcess,
        parameters,
        variableSet,
    };
}
async function getProjectTemplateVariableSet(repository: Repository, projectTemplateSlug: string, gitRef: string): Promise<VariableSetResource> {
    try {
        const [gitVariables, sensitiveVariables] = await Promise.all([
            repository.ProjectTemplates.getVariableSet({
                ProjectTemplateSlug: projectTemplateSlug,
                GitRef: gitRef,
            }),
            getSensitiveVariableSet(repository, projectTemplateSlug),
        ]);
        const variableMap = new Map(gitVariables.Variables.map((v) => [v.Id, v]));
        sensitiveVariables.Variables.forEach((v) => variableMap.set(v.Id, v));
        const allVariables = Array.from(variableMap.values());
        return {
            Id: `project-template-${projectTemplateSlug}`,
            OwnerId: projectTemplateSlug,
            SpaceId: "", // Project templates are platform-hub level
            Version: gitVariables.Version ?? 1,
            Variables: allVariables.map((v): VariableResource => ({
                Id: v.Id,
                Name: v.Name,
                Value: v.Value,
                Description: v.Description,
                Scope: v.Scope,
                Type: v.Type,
                IsEditable: true,
                Prompt: null,
                IsSensitive: v.Type === "Sensitive",
            })),
            ScopeValues: createEmptyScopeValues(),
            Links: {
                Self: "",
            },
        };
    }
    catch (error) {
        logger.info("Variable set file not found, using empty set", error);
        return emptyVariableSetResource("");
    }
}
async function getSensitiveVariableSet(repository: Repository, projectTemplateSlug: string): Promise<ProjectTemplateVariableSetResource> {
    try {
        return await repository.ProjectTemplates.getSensitiveVariableSet(projectTemplateSlug);
    }
    catch (error) {
        // Handle missing sensitive variables gracefully - return empty set
        logger.info("Sensitive variable set not found, using empty set", error);
        return {
            ProjectTemplateSlug: projectTemplateSlug,
            Variables: [],
            Version: 0,
        };
    }
}
function createEmptyScopeValues() {
    return {
        Environments: [],
        Machines: [],
        Actions: [],
        Roles: [],
        Channels: [],
        TenantTags: [],
        Processes: [],
    };
}
const defaultLookups: ProcessStepsLayoutLoaderLookupData = {
    includedScriptModules: [],
    lifecyclePreview: null,
    environmentsById: {},
    environmentsV2ById: {},
    channelsById: null,
    tagSets: [],
    workerPoolsById: {},
    machineRoles: [],
    tagIndex: {},
    userOnboarding: null,
    workerPoolsSummary: {
        WorkerPoolSummaries: [],
        TotalMachines: 0,
        TotalDisabledMachines: 0,
        MachineHealthStatusSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        MachineEndpointSummaries: {
            None: 0,
            TentaclePassive: 0,
            TentacleActive: 0,
            Ssh: 0,
            OfflineDrop: 0,
            AzureWebApp: 0,
            AzureCloudService: 0,
            AzureServiceFabricCluster: 0,
            Kubernetes: 0,
            KubernetesTentacle: 0,
            StepPackage: 0,
        },
        DeploymentTargetSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        TentacleUpgradesRequired: false,
        MachineIdsForCalamariUpgrade: [],
        MachineIdsForTentacleUpgrade: [],
    },
    projectTriggers: [],
    processVariableSet: emptyVariableSetResource(""),
    libraryVariableSets: [],
    templates: [],
    teams: [],
    projects: [],
};
interface ProcessOverviewContentProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    showProcessOverviewStepNotes: boolean;
}
function ProcessOverviewContent({ lookupData, doBusyTask, gitRefResource, showProcessOverviewStepNotes }: ProcessOverviewContentProps) {
    const location = useLocation();
    return <ProcessList lookups={lookupData} doBusyTask={doBusyTask} location={location} gitRefResource={gitRefResource} showStepNotes={showProcessOverviewStepNotes}/>;
}
interface ProcessEditorContentProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    gitRefResource: GitRefResource | undefined;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    onCustomStepTemplateAdded?: (stepTemplate: ActionTemplateResource, result: AddStepTemplateResult) => void;
}
function ProcessEditorContent({ lookupData, busy, doBusyTask, errors, gitRefResource, scrollAreaRef, onCustomStepTemplateAdded }: ProcessEditorContentProps) {
    const isBuiltInWorkerEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled);
    return (<ProcessStepsLayout gitRef={gitRefResource?.CanonicalName} titleAccessory={undefined} stepSlug={undefined} setShowK8sStatusItem={undefined} onProcessSave={() => Promise.resolve()} changeGitRef={noOp} refreshModel={() => Promise.resolve(true)} includedLibraryVariableSetIds={[]} releaseCreationPackageStepId={undefined} lookups={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource} isTenanted={true} onValidationError={() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" })} onCustomStepTemplateAdded={onCustomStepTemplateAdded}/>);
}
export function ProjectTemplateDeploymentProcessPage({ loaderData, gitRef, queryParams, projectTemplateContext }: ProjectTemplateDeploymentProcessPageProps) {
    const navigation = useSpaceAwareNavigation();
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress: busy, errors: busyErrors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const client = useOctopusClient();
    const projectTemplateStatus = useProjectTemplateStatusContext();
    const { projectTemplate, persistenceSettings } = projectTemplateContext;
    const deploymentProcessState = useProjectTemplateDeploymentProcessState(projectTemplate, loaderData.deploymentProcess, gitRef);
    const { deploymentProcessIdentifier, deploymentProcess, reloadDeploymentProcess, modifyDeploymentProcess } = deploymentProcessState;
    const [lookups, setLookups] = useState<ProcessStepsLayoutLoaderLookupData>(() => ({
        ...defaultLookups,
        processVariableSet: loaderData.variableSet,
    }));
    const [cleanModel, setCleanModel] = useState<ProjectTemplateProcessResource>(deploymentProcess);
    const [model, setModel] = useState<ProjectTemplateProcessResource>(deploymentProcess);
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const [producerProjectTemplateContextProps, setProducerProjectTemplateContextProps] = useState<ProducerProjectTemplateContextProps>(() => {
        return buildProducerProjectTemplateContextProps(projectTemplate, loaderData.parameters, loaderData.variableSet);
    });
    const repository = useMemo(() => new Repository(client, {
        abortSignal: new AbortController().signal,
        correlationContext: { [semanticConventions.componentName]: "ProjectTemplateDeploymentProcessPage" },
    }), [client]);
    const refreshLookups = useCallback(async () => {
        const environments = await repository.Environments.all();
        const tenantTagSets = tagSets.getAllByScopes(["Tenant"]);
        const projectTemplateVariableSet = await getProjectTemplateVariableSet(repository, projectTemplate.Slug, gitRef);
        setLookups({
            ...defaultLookups,
            environmentsById: keyBy(environments, "Id"),
            environmentsV2ById: keyBy(environments.map(mapFromEnvironmentResourceToEnvironmentV2Resource), "Id"),
            tagSets: await tenantTagSets,
            machineRoles: await repository.MachineRoles.all(),
            tagIndex: await tagSets.getTagIndex(["Tenant"]),
            processVariableSet: projectTemplateVariableSet,
            libraryVariableSets: [],
        });
        // Update context with refreshed variable names
        setProducerProjectTemplateContextProps(buildProducerProjectTemplateContextProps(projectTemplate, loaderData.parameters, projectTemplateVariableSet));
    }, [repository, projectTemplate, loaderData.parameters, gitRef]);
    useDoBusyTaskEffect(doBusyTask, refreshLookups, [refreshLookups]);
    useEffect(() => {
        setCleanModel(deploymentProcess);
        setModel(deploymentProcess);
    }, [deploymentProcess]);
    const navigateToGitRef = useCallback((newGitRef: string) => {
        const isEditorPage = "actionId" in queryParams || "stepTemplates" in queryParams || "actionType" in queryParams;
        const routeParams = { slug: projectTemplate.Slug, gitRef: newGitRef };
        if (isEditorPage) {
            navigation.redirect(links.projectTemplateDeploymentProcessEditorPage.generateUrl(routeParams, queryParams));
        }
        else {
            navigation.redirect(links.projectTemplateDeploymentProcessPage.generateUrl(routeParams, queryParams));
        }
        lastAccessedPlatformHubGitRef.save(newGitRef);
    }, [navigation, projectTemplate.Slug, queryParams]);
    const supportedActions = (): ProcessPageSupportedActions => {
        return {
            refreshLookupData: refreshLookups,
        };
    };
    const { execute: saveProjectTemplateParametersSet } = useMutation({
        name: "Save project template parameters set",
        action: async (repository: Repository, command: ModifyProjectTemplateParameterSetCommand) => {
            await repository.ProjectTemplates.modifyParameterSet(command);
        },
    });
    const initialFilter = createDefaultFilter();
    // Check if this is editor query params (has editor-specific properties)
    if ("actionId" in queryParams || "stepTemplates" in queryParams || "actionType" in queryParams) {
        const editorParams = queryParams;
        if (editorParams.actionId) {
            initialFilter.actionId = editorParams.actionId;
        }
        if (editorParams.parentStepId) {
            initialFilter.parentStepId = editorParams.parentStepId;
        }
        if (editorParams.actionType) {
            initialFilter.actionType = editorParams.actionType;
        }
        if (editorParams.new !== undefined) {
            initialFilter.new = editorParams.new.toString();
        }
        if (editorParams.stepTemplates !== undefined) {
            initialFilter.stepTemplates = editorParams.stepTemplates ? "1" : "";
        }
        if (editorParams.childStepTemplates !== undefined) {
            initialFilter.childStepTemplates = editorParams.childStepTemplates ? "1" : "";
        }
        if (editorParams.templateId) {
            initialFilter.templateId = editorParams.templateId;
        }
        if (editorParams.templateSpaceId) {
            initialFilter.templateSpaceId = editorParams.templateSpaceId;
        }
        if (editorParams.isCommunityTemplate !== undefined) {
            initialFilter.isCommunityTemplate = editorParams.isCommunityTemplate ? "true" : "false";
        }
        if (editorParams.actionCategory) {
            initialFilter.actionCategory = editorParams.actionCategory;
        }
    }
    return (<ProducerProjectTemplateContext.Provider value={producerProjectTemplateContextProps}>
            <PersistenceSettingsContextProvider {...persistenceSettings}>
                <ProcessController layoutActions={supportedActions()} doBusyTask={doBusyTask} processIdentifier={deploymentProcessIdentifier} process={cleanModel} reloadProcess={reloadDeploymentProcess} modifyProcess={modifyDeploymentProcess}>
                    {() => (<ProcessQueryStringController initialQueryFilter={initialFilter}>
                            {() => (<ProjectTemplateDeploymentProcessPageInternal projectTemplateContext={projectTemplateContext} cleanModel={cleanModel} model={model} onChange={setModel} lookups={lookups} doBusyTask={doBusyTask} busy={busy} errors={busyErrors} gitRef={gitRef} setGitRef={navigateToGitRef} scrollAreaRef={scrollAreaRef} modifyDeploymentProcess={modifyDeploymentProcess} projectTemplateStatus={projectTemplateStatus} parameterSet={loaderData.parameters} variableSet={loaderData.variableSet} setProducerProjectTemplateContextProps={setProducerProjectTemplateContextProps} saveProjectTemplateParametersSet={saveProjectTemplateParametersSet}/>)}
                        </ProcessQueryStringController>)}
                </ProcessController>
            </PersistenceSettingsContextProvider>
        </ProducerProjectTemplateContext.Provider>);
}
interface ProjectTemplateDeploymentProcessPageInternalProps {
    projectTemplateContext: ProjectTemplateContextProps;
    cleanModel: ProjectTemplateProcessResource;
    model: ProjectTemplateProcessResource;
    onChange: (model: ProjectTemplateProcessResource) => void;
    lookups: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    busy: boolean;
    errors: Error[];
    gitRef: string;
    setGitRef: (gitRef: string) => void;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    modifyDeploymentProcess: (process: ModifyProjectTemplateProcessCommand, gitRef: string | undefined) => Promise<void>;
    projectTemplateStatus: ProjectTemplateStatus | undefined;
    parameterSet: ProjectTemplateParameterSetResource;
    variableSet: VariableSetResource;
    setProducerProjectTemplateContextProps: (newState: ProducerProjectTemplateContextProps) => void;
    saveProjectTemplateParametersSet: (command: ModifyProjectTemplateParameterSetCommand) => Promise<void>;
}
function ProjectTemplateDeploymentProcessPageInternal({ projectTemplateContext, cleanModel, model, onChange, lookups, doBusyTask, busy, errors, gitRef, setGitRef, scrollAreaRef, modifyDeploymentProcess, projectTemplateStatus, parameterSet, variableSet, setProducerProjectTemplateContextProps, saveProjectTemplateParametersSet, }: ProjectTemplateDeploymentProcessPageInternalProps) {
    const processContext = useProcessContext();
    const hasValidProcess = processContext.selectors.hasValidProcess();
    const hasSteps = processContext.selectors.hasSteps();
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const errorSelectors = useProcessErrorSelectors();
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const { openDialog: openCommitDialog, isOpen: isCommitDialogOpen, closeAllDialogs: closeCommitDialog } = useDialogTrigger();
    const processSearchFilterContext = useProcessSearchFilterContext();
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    const [showProcessOverviewStepNotes, setShowProcessOverviewStepNotes] = useState(true);
    const processQueryStringContext = useProcessQueryStringContext();
    const queryFilter = processQueryStringContext.state.queryFilter;
    const intent = intentFromFilter(queryFilter);
    const [latestImportedStepTemplate, setLatestImportedStepTemplate] = useState<ImportStepTemplateResult | null>(null);
    const producerTemplateProcessContext = useProducerTemplateProcessContext();
    const { projectTemplate, persistenceSettings, gitRefResource, projectTemplateLink, publishedVersions, refetchPublishedVersions } = projectTemplateContext;
    const [showInitialSuccessCallout, setShowInitialSuccessCallout] = useState(false);
    const { isOpen: isWizardOpen, openWizard, dialogProps } = useProjectTemplateWizard();
    const { isShared: isTemplateShared, refetchIsShared: refetchIsTemplateShared } = useIsProjectTemplateShared(projectTemplate.Slug);
    const { openDialog: openStopShareDialog, isOpen: isStopShareDialogOpen, closeDialog: closeStopShareDialog } = useDialogTrigger();
    const { handleStopShare } = useStopShareProjectTemplate(projectTemplate, refetchIsTemplateShared);
    const { executeSave } = useSaveProjectTemplate({
        projectTemplate,
        gitRef,
        commitHandler: dialogProps.commitHandler,
        onClose: dialogProps.onClose,
        refetchPublishedVersions,
        refetchIsTemplateShared,
        setShowInitialSuccessCallout,
    });
    const handleCommit = async (process: ModifyProjectTemplateProcessCommand, parameterSet: ModifyProjectTemplateParameterSetCommand, gitRef: string | undefined): Promise<void> => {
        try {
            await modifyDeploymentProcess(process, gitRef);
            await saveProjectTemplateParametersSet(parameterSet);
            errorActions.clearErrors();
            warningActions.clearWarnings();
            showSnackbar("Successfully committed changes to the deployment process.");
            // Check if the process now has steps and dispatch action to update status bar
            if (process.Steps && process.Steps.length > 0 && projectTemplateStatus) {
                projectTemplateStatus.dispatch({
                    type: projectTemplateStatusActions.projectTemplateDeploymentProcessAdded,
                });
            }
        }
        catch (error) {
            errorActions.setErrors(getErrorsFromError(error), processContext.selectors);
            warningActions.clearWarnings();
            throw error;
        }
    };
    function getDefaultCommitMessage(): string {
        return "Update deployment process";
    }
    const getProcessToCommit = (): ModifyProjectTemplateProcessCommand => {
        const { modelProcess: clientProcessResource } = processContext.selectors.getProcessesForMerge();
        applyCommonLogicToProducerProcessResource(clientProcessResource, lookups.workerPoolsSummary, processContext.selectors.getActionPlugin, ProcessType.ProjectTemplate);
        return {
            ...model,
            Steps: clientProcessResource.Steps,
            ChangeDescription: getDefaultCommitMessage(),
        };
    };
    const getParameterSetToCommit = (): ModifyProjectTemplateParameterSetCommand => {
        return {
            ProjectTemplateSlug: projectTemplate.Slug,
            GitRef: gitRef,
            ParameterSet: producerTemplateProcessContext?.parameters
                ? {
                    ...parameterSet,
                    Parameters: producerTemplateProcessContext?.parameters,
                }
                : parameterSet,
            ChangeDescription: "Update project template parameters",
        };
    };
    const createCommitHandler = () => async (commitMessage: string) => {
        const processToCommit = getProcessToCommit();
        const parameterSetToCommit = getParameterSetToCommit();
        processToCommit.ChangeDescription = commitMessage;
        await handleCommit(processToCommit, parameterSetToCommit, gitRef);
    };
    const saveProcess = async (isNavigationConfirmation: boolean) => {
        if (isNavigationConfirmation) {
            openCommitDialog();
        }
    };
    const onCustomStepTemplateAdded = useCallback((stepTemplate: ActionTemplateResource, result: AddStepTemplateResult) => {
        const newProcessTemplateParameters = convertActionTemplateParametersToTemplateParameters(stepTemplate.Parameters);
        const existingParameterNames = new Set(parameterSet.Parameters.map((p) => p.Name));
        const uniqueNewParameters = newProcessTemplateParameters.filter((p) => !existingParameterNames.has(p.Name));
        if (uniqueNewParameters.length > 0) {
            const updatedParameterSet: ProjectTemplateParameterSetResource = {
                ...parameterSet,
                Parameters: [...parameterSet.Parameters, ...uniqueNewParameters],
            };
            setProducerProjectTemplateContextProps(buildProducerProjectTemplateContextProps(projectTemplate, updatedParameterSet, variableSet));
        }
        setLatestImportedStepTemplate({ stepTemplate, addedParameters: uniqueNewParameters, duplicateParameters: newProcessTemplateParameters.filter((p) => existingParameterNames.has(p.Name)), addResult: result });
    }, [projectTemplate, parameterSet, variableSet, setProducerProjectTemplateContextProps]);
    const processFromContext = useMemo(() => (processContext.state.model.process ? processContext.selectors.getProcessResource() : null), [processContext.selectors, processContext.state.model.process]);
    useEffect(() => {
        if (!processFromContext) {
            return;
        }
        if (!isEqual(model.Steps, processFromContext.Steps)) {
            const resource: ProjectTemplateProcessResource = { ...model, Steps: processFromContext.Steps };
            onChange(resource);
        }
    }, [processFromContext, model, onChange]);
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (isTemplateShared) {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish], undefined, createCommitHandler());
                }
                else {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish, TemplateWizardSteps.Share], undefined, createCommitHandler());
                }
            }} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy} onClick={() => handleCommit(getProcessToCommit(), getParameterSetToCommit(), gitRef)}/>
                </div>
            </PermissionCheck>),
    };
    const publishButton: PageAction = {
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ProjectTemplatePublishButton onPublish={() => openWizard([TemplateWizardSteps.Publish])}/>
            </PermissionCheck>),
        key: "Publish",
    };
    const addStepButton: PageAction = {
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ContextAddStepButton />
            </PermissionCheck>),
        key: "Add Step",
    };
    const overflowActions = createProjectTemplateOverflowMenuItems({ projectTemplateLink, onShare: () => openWizard([TemplateWizardSteps.Share]), isShared: isTemplateShared, onStopShare: openStopShareDialog });
    const processFilters = hasValidProcess && hasSteps && intent === ProcessPageIntent.Unknown
        ? {
            inputs: [
                <FilterSearchBox key="filter-search" placeholder="Filter by name..." value={processSearchFilterContextLookupsState.searchFilter.filterKeyword} onChange={(filterKeyword) => processSearchFilterContextActions.onFilterChange((prev) => ({ ...prev, filterKeyword }))} autoFocus={true}/>,
                <Checkbox key="show-notes" label={"Show notes"} value={showProcessOverviewStepNotes} onChange={setShowProcessOverviewStepNotes} noMargin={true}/>,
            ],
            advancedFilters: {
                content: <ProcessListAdvancedFilters processType={ProcessType.Deployment} environmentLookupData={lookups.environmentsById}/>,
                onResetFilter: () => processSearchFilterContextActions.onFilterChange(() => processSearchFilterContext.getEmptyFilter()),
                hasUserSelectedValues: processSearchFilterContext.isFiltering,
            },
        }
        : undefined;
    const callout = GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName: projectTemplate.Name });
    return (<>
            <LegacyForm devToolsDirtyTrackingKey="Project Template Deployment Process" disableDirtyFormChecking={disableDirtyFormChecking} onSaveClick={saveProcess} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={busy}>
                {({ FormContent }) => (<PageContent busy={busy} header={{
                title: intent === ProcessPageIntent.Unknown ? "Process" : "Process Editor",
                breadcrumbs: intent !== ProcessPageIntent.Unknown
                    ? [
                        {
                            label: "Process",
                            pageUrl: links.projectTemplateDeploymentProcessPage.generateUrl({
                                slug: projectTemplate.Slug,
                                gitRef: gitRef || "main",
                            }),
                        },
                    ]
                    : undefined,
                showBreadcrumbBackIcon: intent !== ProcessPageIntent.Unknown,
                pageActions: intent === ProcessPageIntent.Unknown
                    ? [publishButton, addStepButton]
                    : intent === ProcessPageIntent.ChooseStepTemplates || intent === ProcessPageIntent.ChooseChildStepTemplates
                        ? [commitButton]
                        : [publishButton, addStepButton, commitButton],
                contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/>,
                overflowActions,
            }} filters={processFilters} callout={callout}>
                        <div className={styles.contentWrapper}>
                            {intent === ProcessPageIntent.Unknown ? (<ProcessOverviewContent lookupData={lookups} doBusyTask={doBusyTask} gitRefResource={gitRefResource} showProcessOverviewStepNotes={showProcessOverviewStepNotes}/>) : (<>
                                    {latestImportedStepTemplate ? (<StepTemplateImportedCallout onClose={() => setLatestImportedStepTemplate(null)} latestImportedStepTemplate={latestImportedStepTemplate} processType={processContext.selectors.getProcessType()}/>) : null}
                                    <ProcessEditorContent lookupData={lookups} busy={busy} doBusyTask={doBusyTask} errors={errors} gitRefResource={gitRefResource} scrollAreaRef={scrollAreaRef} onCustomStepTemplateAdded={onCustomStepTemplateAdded}/>
                                </>)}
                        </div>
                    </PageContent>)}
            </LegacyForm>

            <ProjectTemplateCommitDialog open={isCommitDialogOpen} gitRef={gitRef} onClose={closeCommitDialog} onCommit={async (processCommand: ModifyProjectTemplateProcessCommand, gitRef: string | undefined) => {
            const parameterSetToCommit = getParameterSetToCommit();
            await handleCommit(processCommand, parameterSetToCommit, gitRef);
        }} getCommandToCommit={getProcessToCommit} defaultCommitMessage={getDefaultCommitMessage()} entityName="deployment process"/>

            {isWizardOpen && (<ProjectTemplateWizardDialog projectTemplate={projectTemplate} gitRef={gitRef} defaultBranch={persistenceSettings.DefaultBranch} stepsToShow={dialogProps.stepsToShow} getDefaultCommitMessage={() => getDefaultCommitMessage()} onClose={dialogProps.onClose} onInitialSuccessCallout={() => setShowInitialSuccessCallout(true)} publishedVersions={publishedVersions} executeSave={executeSave}/>)}

            <Dialog open={isStopShareDialogOpen}>
                <StopShareTemplateDialog onSave={handleStopShare} onClose={closeStopShareDialog} templateType={PlatformHubTemplateType.ProjectTemplate}/>
            </Dialog>
        </>);
}
const styles = {
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
    contentWrapper: css({
        borderTop: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
};
